import React from "react";
import moment from "moment";
import Select from "../../components/shared/form/ReactSelect";
import Page from "../../components/shared/pages/Page";
import Loading from "../../components/shared/loaders/Loading";
import store from "../../Store";
import { setDashboardMenu } from "../../routes/main-route/MainRoute-Actions";
import {
  fetchPlans,
  fetchSubscriptions,
  isNotDeployed,
  unwrapList,
} from "./billingApi";

const STATUSES = ["active", "past_due", "canceled", "incomplete", "selected"];
const STATUS_OPTIONS = STATUSES.map((s) => ({ value: s, label: s }));

const STATUS_TONE = {
  active: "ok",
  past_due: "warn",
  canceled: "err",
  incomplete: "off",
  selected: "info",
};

const PLAN_OPTIONS = [
  { value: "lite", label: "Light" },
  { value: "plus", label: "Plus" },
  { value: "pro", label: "Pro" },
];

// Screen 2 — one row per organization with a subscription, from the local
// Stripe mirror (webhooks keep it current). Stripe owns invoices/dunning —
// the row deep-links there instead of rebuilding them.
class BillingSubscriptions extends React.Component {
  state = {
    loading: true,
    notDeployed: false,
    error: "",
    rows: [],
    planPrices: {}, // slug -> display price (for indicative MRR)
    status: "",
    plan: "",
    q: "",
  };

  componentDidMount() {
    document.querySelector("body").classList.add("organization-dashboard");
    store.dispatch(setDashboardMenu(""));

    fetchPlans()
      .then((res) => {
        // slug -> {CURRENCY: amount} from the per-currency price rows
        const planPrices = {};
        unwrapList(res).forEach((p) => {
          if (!p.slug) return;
          planPrices[p.slug] = {};
          (p.prices || []).forEach((pr) => {
            planPrices[p.slug][(pr.currency || "").toUpperCase()] = pr.amount;
          });
        });
        this.setState({ planPrices });
      })
      .catch(() => {});
    this.load();
  }

  componentWillUnmount() {
    document.querySelector("body").classList.remove("organization-dashboard");
  }

  load = () => {
    const { status, q } = this.state;
    this.setState({ loading: true, error: "", notDeployed: false });
    fetchSubscriptions({ status, q })
      .then((res) => this.setState({ rows: unwrapList(res), loading: false }))
      .catch((err) => {
        if (isNotDeployed(err)) {
          this.setState({ notDeployed: true, loading: false });
        } else {
          this.setState({
            error: "Could not load subscriptions.",
            loading: false,
          });
        }
      });
  };

  onSearchKey = (e) => {
    if (e.key === "Enter") this.load();
  };

  stripeUrl(row) {
    if (row.stripe_subscription_id)
      return `https://dashboard.stripe.com/subscriptions/${row.stripe_subscription_id}`;
    if (row.stripe_customer_id)
      return `https://dashboard.stripe.com/customers/${row.stripe_customer_id}`;
    return null;
  }

  mrr(row) {
    const qty = row.quantity;
    if (!qty) return "—";
    const currency = (row.currency || "SEK").toUpperCase();
    const amounts = this.state.planPrices[row.plan] || {};
    const price = row.price != null ? row.price : amounts[currency];
    if (price == null) return `${qty} × ?`;
    const label = currency === "SEK" ? "kr" : currency;
    return `${qty} × ${parseFloat(price)} ${label}`;
  }

  render() {
    const { loading, notDeployed, error, rows, status, plan, q } = this.state;
    const shown = plan ? rows.filter((r) => (r.plan || "") === plan) : rows;

    return (
      <Page title="Subscriptions">
        <div className="heading-block pr-60">
          <div className="title-block">
            <h2>Subscriptions</h2>
            <p className="billing-sub">
              Billing status per organization, mirrored from Stripe via
              webhooks. Quantity = number of billable sites; MRR is indicative.
            </p>
          </div>
          <div className="search-bar">
            <input
              type="text"
              className="form-control"
              placeholder="Search org, customer or email"
              value={q}
              onChange={(e) => this.setState({ q: e.target.value })}
              onKeyDown={this.onSearchKey}
              onBlur={this.load}
            />
            <span className="icon-search" />
          </div>
          <div className="billing-filters">
            <Select
              value={STATUS_OPTIONS.find((o) => o.value === status) || null}
              isClearable
              placeholder="All statuses"
              options={STATUS_OPTIONS}
              onChange={(selected) =>
                this.setState(
                  { status: selected ? selected.value : "" },
                  this.load,
                )
              }
              noFormGroup
            />
            <Select
              value={PLAN_OPTIONS.find((o) => o.value === plan) || null}
              isClearable
              placeholder="All plans"
              options={PLAN_OPTIONS}
              onChange={(selected) =>
                this.setState({ plan: selected ? selected.value : "" })
              }
              noFormGroup
            />
          </div>
        </div>

        {notDeployed ? (
          <div className="billing-notice">
            The billing endpoints are not deployed yet (portal-api&nbsp;·{" "}
            <code>stripe-ground-zero</code>). This screen will populate once{" "}
            <code>GET /admin/billing/subscriptions</code> is live.
          </div>
        ) : error ? (
          <div className="billing-notice billing-notice--error">{error}</div>
        ) : null}

        <div className="table-block table-responsive pr-60">
          <table className="table">
            <thead>
              <tr>
                <th>Org / Customer</th>
                <th>Plan</th>
                <th>Status</th>
                <th>Sites (qty)</th>
                <th>MRR</th>
                <th>Current period ends</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="text-center">
                    <Loading />
                  </td>
                </tr>
              ) : shown.length ? (
                shown.map((row, i) => {
                  const st = row.stripe_status || row.status || "—";
                  const link = this.stripeUrl(row);
                  return (
                    <tr key={i}>
                      <td>
                        {row.organization_name || row.org || "—"}
                        {row.customer_email ? (
                          <div className="billing-muted">
                            {row.customer_email}
                          </div>
                        ) : null}
                      </td>
                      <td>{row.plan_title || row.plan || "—"}</td>
                      <td>
                        <span
                          className={`billing-badge ${STATUS_TONE[st] || "off"}`}
                        >
                          {st}
                        </span>
                        {st === "selected" && (
                          <div className="billing-muted">
                            plan chosen, no live subscription yet
                          </div>
                        )}
                      </td>
                      <td>{row.quantity != null ? row.quantity : "—"}</td>
                      <td>{this.mrr(row)}</td>
                      <td>
                        {row.current_period_end
                          ? moment(row.current_period_end).format("YYYY-MM-DD")
                          : "—"}
                      </td>
                      <td className="text-center">
                        {link ? (
                          <a
                            href={link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="billing-link-btn"
                            title="Open in the Stripe dashboard (invoices, receipts, dunning)"
                          >
                            ↗ Stripe
                          </a>
                        ) : null}
                      </td>
                    </tr>
                  );
                })
              ) : !notDeployed ? (
                <tr>
                  <td colSpan={7} className="text-center">
                    No subscriptions match
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </Page>
    );
  }
}

export default BillingSubscriptions;
